/**
 * MED-1269 — contract between EditCohortDetailsModal and the self-contained
 * sections that used to autosave (Audience Feature Controls, Email Settings,
 * Session Time Slot Options).
 *
 * Each section holds its pending changes locally and registers a controller
 * with the modal. The modal's footer Save commits every section in one user
 * action (destructive writes sequenced last), shows one future-tense summary
 * for destructive changes, and guards close against unsaved changes.
 */

/** A pending destructive change, described in the future tense for the
 * save-time summary ("what Save will do"). */
export interface DestructiveChange {
  /** Short label shown as the summary line item */
  label: string;
  /** Optional detail lines under the label */
  details?: string[];
}

export interface SectionController {
  /** Value-based: true only when pending state differs from as-loaded state */
  dirty: boolean;
  /** Pending destructive changes for the save-time summary (AC11–AC12) */
  destructive: DestructiveChange[];
  /**
   * Persist pending changes and reset the section's baseline to the saved
   * values. Throws on failure — the section must keep its pending state so
   * the user can retry (AC19).
   */
  commit: () => Promise<void>;
  /**
   * Optional pre-save check. Returns false when the section's pending state
   * must not be written; the section shows its own error. The modal blocks
   * the save and opens the section, the same way core validation blocks it.
   */
  validate?: () => boolean;
}

export type SectionKey = 'emailSettings' | 'timeSlots' | 'featureToggles';

export type SectionRegister = (controller: SectionController | null) => void;

/** Human labels for per-section save outcome reporting (AC19) */
export const SECTION_LABELS: Record<SectionKey | 'core', string> = {
  core: 'Cohort details',
  emailSettings: 'Email Settings',
  timeSlots: 'Session Time Slot Options',
  featureToggles: 'Audience Feature Controls',
};
